import { produce } from 'immer'
import { IActivity, IContest, IElegibleContests } from 'core/models/Models'
import {
  CONTEST,
  CONTESTS,
  CURRENT_CONTESTS_PAGINATED,
  PAST_CONTESTS_PAGINATED,
  SAVED_CONTESTS_PAGINATED,
  CONTEST_APPLICATION_LIKE,
  CONTEST_CONTEST_APPLICATION_VOTE,
  CONTEST_COMMENTS,
  CONTEST_COMMENT_LIKE,
  CONTEST_DOCUMENT,
  CONTEST_LIKE,
  CONTEST_TAGS,
  CONTEST_VISIBILITY,
  ELIGIBLE_CONTESTS,
  CONTEST_ACTION_VOTE,
  CONTEST_ACTION_LIKE,
  CONTEST_VIEW,
  SEARCH_CONTEST,
  USERS_CONTESTS_PAGINATED,
} from 'core/actions/ContestActions'

export interface ISearchContestResult {
  current: IContest[]
  past: IContest[]
  users: IContest[]
  saved: IContest[]
}

export interface IContestState {
  contests: IContest[]
  currentContests: IContest[]
  pastContests: IContest[]
  usersContests: IContest[]
  savedContests: IContest[]
  searchResult: ISearchContestResult | null
  eligibleContests: IElegibleContests
  contestsLoaded: boolean
  isFinalPage: {
    current: boolean
    past: boolean
    users: boolean
    saved: boolean
  }
}

const initialState: IContestState = {
  contests: [],
  currentContests: [],
  pastContests: [],
  usersContests: [],
  savedContests: [],
  searchResult: null,
  eligibleContests: {
    contests: [],
    count: -1,
  },
  contestsLoaded: false,
  isFinalPage: {
    current: false,
    past: false,
    users: false,
    saved: false,
  },
}

const contestCollectionKeys: Array<
  keyof Pick<
    IContestState,
    'contests' | 'currentContests' | 'pastContests' | 'usersContests' | 'savedContests'
  >
> = ['contests', 'currentContests', 'pastContests', 'usersContests', 'savedContests']

function appendUniqueContests(existingContests: IContest[], newContests: IContest[]) {
  const existingIds = new Set(existingContests.map((item) => item?.attributes?.id))
  const uniqueContests = newContests.filter((item) => !existingIds.has(item?.attributes?.id))
  return [...existingContests, ...uniqueContests]
}

function replaceOrAppendContest(contests: IContest[], payload: IContest) {
  const existingIndex = contests.findIndex((item) => item?.attributes?.id == payload?.attributes?.id)
  if (existingIndex === -1) {
    return [...contests, payload]
  }
  return contests.map((item) => (item?.attributes?.id == payload?.attributes?.id ? payload : item))
}

function replaceContestIfPresent(contests: IContest[], payload: IContest) {
  const existingIndex = contests.findIndex((item) => item?.attributes?.id == payload?.attributes?.id)
  if (existingIndex === -1) {
    return contests
  }
  return contests.map((item) => (item?.attributes?.id == payload?.attributes?.id ? payload : item))
}

function removeContest(contests: IContest[], contestId: number) {
  return contests.filter((item) => item?.attributes?.pId !== contestId)
}

function isPublishedContest(contest: IContest) {
  return Boolean(contest?.attributes?.published)
}

function isCurrentContest(contest: IContest) {
  const today = new Date().toISOString().slice(0, 10)
  return (
    contest?.attributes?.applicationsDeadline >= today ||
    (contest?.attributes?.votingDeadline != null && contest?.attributes?.votingDeadline >= today)
  )
}

function insertContest(contests: IContest[], payload: IContest) {
  const contestsWithoutPayload = removeContest(contests, payload?.attributes?.pId)
  return [payload, ...contestsWithoutPayload]
}

function updateContestCollections(
  state: IContestState,
  draft: IContestState,
  updater: (contests: IContest[]) => IContest[],
) {
  contestCollectionKeys.forEach((key) => {
    draft[key] = updater(state[key])
  })
}

const contestReducer = (state = initialState, action: IActivity): IContestState =>
  produce(state, (draft) => {
    const { type, payload } = action
    switch (type) {
      case CONTESTS.GET.SUCCESS:
        draft.contestsLoaded = true
        draft.contests = payload
        break
      case CURRENT_CONTESTS_PAGINATED.GET.SUCCESS:
        draft.currentContests = appendUniqueContests(state.currentContests, payload.payload)
        draft.isFinalPage.current = payload.isFinalPage
        break
      case PAST_CONTESTS_PAGINATED.GET.SUCCESS:
        draft.pastContests = appendUniqueContests(state.pastContests, payload.payload)
        draft.isFinalPage.past = payload.isFinalPage
        break
      case USERS_CONTESTS_PAGINATED.GET.SUCCESS:
        draft.usersContests = appendUniqueContests(state.usersContests, payload.payload)
        draft.isFinalPage.users = payload.isFinalPage
        break
      case SAVED_CONTESTS_PAGINATED.GET.SUCCESS:
        draft.savedContests = appendUniqueContests(state.savedContests, payload.payload)
        draft.isFinalPage.saved = payload.isFinalPage
        break
      case SEARCH_CONTEST.GET.SUCCESS:
        draft.searchResult = {
          current: payload?.current ?? [],
          past: payload?.past ?? [],
          users: payload?.users ?? [],
          saved: payload?.saved ?? [],
        }
        draft.contestsLoaded = false
        break
      case ELIGIBLE_CONTESTS.GET.SUCCESS:
        draft.eligibleContests = {
          contests: payload,
          count: payload?.length ?? -1,
        }
        break
      case CONTEST.GET.SUCCESS:
      case CONTEST_VIEW.POST.SUCCESS: {
        draft.contests = replaceOrAppendContest(state.contests, payload)
        draft.currentContests = replaceContestIfPresent(state.currentContests, payload)
        draft.pastContests = replaceContestIfPresent(state.pastContests, payload)
        draft.usersContests = replaceContestIfPresent(state.usersContests, payload)
        draft.savedContests = replaceContestIfPresent(state.savedContests, payload)
        break
      }
      case CONTEST.POST.SUCCESS: {
        draft.contests = insertContest(state.contests, payload)

        if (isPublishedContest(payload)) {
          draft.usersContests = insertContest(state.usersContests, payload)
          draft.savedContests = removeContest(state.savedContests, payload?.attributes?.pId)

          if (isCurrentContest(payload)) {
            draft.currentContests = insertContest(state.currentContests, payload)
            draft.pastContests = removeContest(state.pastContests, payload?.attributes?.pId)
          } else {
            draft.pastContests = insertContest(state.pastContests, payload)
            draft.currentContests = removeContest(state.currentContests, payload?.attributes?.pId)
          }
        } else {
          draft.savedContests = insertContest(state.savedContests, payload)
          draft.usersContests = removeContest(state.usersContests, payload?.attributes?.pId)
        }
        break
      }
      case CONTEST.PUT.SUCCESS:
      case CONTEST_TAGS.POST.SUCCESS:
      case CONTEST_DOCUMENT.POST.SUCCESS:
      case CONTEST_DOCUMENT.PUT.SUCCESS:
      case CONTEST_DOCUMENT.DELETE.SUCCESS:
      case CONTEST_VISIBILITY.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) => replaceContestIfPresent(contests, payload))
        break
      case CONTEST.DELETE.SUCCESS:
        updateContestCollections(state, draft, (contests) => removeContest(contests, payload.id))
        break
      case CONTEST_LIKE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((item) => {
            if (item?.attributes?.pId == payload?.elementId) {
              const userId = payload?.userId
              const likedByIds = item?.attributes?.likedByIds ?? []
              const index = likedByIds?.indexOf(userId) ?? -1
              const newElementsById = [...likedByIds]
              if (index > -1) {
                newElementsById.splice(index, 1)
                const attributes = { ...item.attributes, likedByIds: newElementsById }
                return { ...item, attributes: attributes }
              } else {
                newElementsById.push(userId)
                const attributes = { ...item.attributes, likedByIds: newElementsById }
                return { ...item, attributes: attributes }
              }
            }
            return item
          }),
        )
        break
      case CONTEST_COMMENTS.PUT.SUCCESS:
      case CONTEST_COMMENT_LIKE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((contest) =>
            contest.attributes.pId === payload.elementId
              ? { ...contest, comments: contest.comments?.map((c) => (c.pId === payload.pId ? payload : c)) }
              : contest,
          ),
        )
        break
      case CONTEST_COMMENTS.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((contest) => {
            if (contest.attributes?.pId === payload.elementId) {
              const newComments = [...contest.comments]
              newComments?.push(payload)
              return { ...contest, comments: newComments }
            }
            return contest
          }),
        )
        break
      case CONTEST_COMMENTS.DELETE.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((contest) => {
            if (contest.attributes.pId === payload.elementId) {
              const comments = [...contest.comments].filter(
                (c) => c.pId !== payload.pId && c?.parentId !== payload.pId,
              )
              return { ...contest, comments }
            }
            return contest
          }),
        )
        break
      case CONTEST_APPLICATION_LIKE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((item) => {
            if (item?.attributes?.pId == payload?.contestId) {
              const platformApplications = item.platformApplications.map((application) => {
                if (application?.pId == payload?.elementId) {
                  const userId = payload?.userId
                  const likedByIds = application?.likedByIds ?? []
                  const index = likedByIds?.indexOf(userId) ?? -1
                  const newElementsById = [...likedByIds]
                  if (index > -1) {
                    newElementsById.splice(index, 1)
                    return { ...application, likedByIds: newElementsById }
                  } else {
                    newElementsById.push(userId)
                    return { ...application, likedByIds: newElementsById }
                  }
                }
                return application
              })
              return { ...item, platformApplications: platformApplications }
            }
            return item
          }),
        )
        break
      case CONTEST_CONTEST_APPLICATION_VOTE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((item) => {
            if (item?.attributes?.pId == payload?.contestId) {
              const platformApplications = item.platformApplications.map((application) => {
                if (application?.pId == payload?.contestApplicationId) {
                  const userId = payload?.userId
                  const backedByIds = application?.backedByIds ?? []
                  const index = backedByIds?.indexOf(userId) ?? -1
                  const newElementsById = [...backedByIds]
                  if (index > -1) {
                    newElementsById.splice(index, 1)
                    return { ...application, backedByIds: newElementsById }
                  } else {
                    newElementsById.push(userId)
                    return { ...application, backedByIds: newElementsById }
                  }
                }
                return application
              })
              return { ...item, platformApplications: platformApplications }
            }
            return item
          }),
        )
        break
      case CONTEST_ACTION_LIKE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((item) => {
            if (item?.attributes?.pId == payload?.contestId) {
              const actionApplications = item.actionApplications.map((application) => {
                if (application?.pId == payload?.elementId) {
                  const userId = payload?.userId
                  const likedByIds = application?.likedByIds ?? []
                  const index = likedByIds?.indexOf(userId) ?? -1
                  const newElementsById = [...likedByIds]
                  if (index > -1) {
                    newElementsById.splice(index, 1)
                    return { ...application, likedByIds: newElementsById }
                  } else {
                    newElementsById.push(userId)
                    return { ...application, likedByIds: newElementsById }
                  }
                }
                return application
              })
              return { ...item, actionApplications: actionApplications }
            }
            return item
          }),
        )
        break
      case CONTEST_ACTION_VOTE.POST.SUCCESS:
        updateContestCollections(state, draft, (contests) =>
          contests.map((item) => {
            if (item?.attributes?.pId == payload?.attributes?.contestId) {
              const actionApplications = item.actionApplications.map((application) => {
                if (application?.pId == payload?.attributes?.pId) {
                  return payload?.attributes
                }
                return application
              })
              return { ...item, actionApplications: actionApplications }
            }
            return item
          }),
        )
        break
      default:
        return draft
    }
  })

export default contestReducer
